import { NoteAdd } from "@mui/icons-material";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { color, Stack } from "@mui/system";
import { getUrl } from "aws-amplify/storage";
import { FileDown } from "lucide-react";
import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTheme } from "styled-components";
import ReportWrapper from "../../abstract/Report";
import DefaultLogo from "../../assets/images/report-default-logo.png";
import LightBulbBlack from "../../assets/images/report_icons/lightbulb-black.png";
import LightBulbBlue from "../../assets/images/report_icons/lightbulb-blue.png";
import LightBulbGreen from "../../assets/images/report_icons/lightbulb-green.png";
import LightBulbOrange from "../../assets/images/report_icons/lightbulb-orange.png";
import LightBulbPink from "../../assets/images/report_icons/lightbulb-pink.png";
import LightBulbPurple from "../../assets/images/report_icons/lightbulb-purple.png";
import LightBulbRed from "../../assets/images/report_icons/lightbulb-red.png";
import LightBulbYellow from "../../assets/images/report_icons/lightbulb-yellow.png";
import NotesDialog from "../../components/dialogs/NotesDialog";
import AverageEggsChart, {
  AverageEggsChartData,
} from "../../components/report/charts/AverageEggsChart";
import ProcessSteps from "../../components/report/charts/ProcessSteps";
import CycleCard from "../../components/report/CycleCard";
import CycleTable from "../../components/report/CycleTable";
import { useApp } from "../../contexts/AppContext";
import { useAuth } from "../../contexts/AuthContext";
import { useError } from "../../contexts/ErrorContext";
import { useReport } from "../../contexts/ReportContext";
import { useResource } from "../../contexts/ResourceContext";
import generalTranslations from "../../lang/en/general.json";
import PDF from "../../pdf/PDF";
import { colors, ReportColorStyle, ReportColorStyleName } from "../../theme";
import { reportPageWidth, EthnicityType } from "../../types";
import { enumKeyByValue, getCurrentColorScheme } from "../../utils";
import InfoCard from "./InfoCard";
import PatientInfoCard from "./PatientInfoCard";
import { FormData, FormResponse, ReportOverrideFooter } from "./types";
import { useApi } from "../../contexts/ApiContext";
import Progress from "../../components/Progress";

const Report: React.FC = () => {
  const [logo, setLogo] = React.useState<string>(DefaultLogo);
  const [contactDetails, setContactDetails] =
    React.useState<ReportOverrideFooter>({
      address:
        "Unit G-2, Ground Floor, The Podium Tower 3, UOA. Business Park, No 1, Jalan Pengaturcara",
      phone: "+60 3-5888 8335",
      website: "generation",
    });
  const [selectedSections, setSelectedSections] = React.useState<{
    [key: string]: boolean;
  }>({
    estimatedResults: true,
    estimatedResultsIn5Years: true,
    chartAttritionRates: true,
    chartEggsByAge: true,
  });
  const { toggleNotesDialog, colorStyle, updateColorStyle } = useReport();

  const { user } = useAuth();
  const { updateBreadcrumbs, updateActions, translate } = useApp();
  const translations = useMemo(() => translate("eggfreeze"), [translate]);
  const navigate = useNavigate();
  const theme = useTheme();
  const { showError } = useError();
  const [isLoading, setLoading] = React.useState<boolean>(false);
  const [notes, setNotes] = React.useState<string>("");

  const footerRef = useRef<HTMLDivElement>(null);
  const { response } = useResource<FormData, FormResponse>();

  const location = useLocation();
  const formData: FormData = location.state?.formData;

  if (!formData) {
    showError(translations.report.noFormDataFound);
    navigate("/calc");
  }

  const ethnicities = (formData.ethnicity ?? []).map((e) =>
    translate("general", `ethnicities.${enumKeyByValue(EthnicityType, e)}`)
  );
  
  useEffect(() => {
    if (!user) {
      navigate("/login");
    }
    if (user.config?.logo) {
      getUrl({ path: user.config.logo }).then((urlResponse) => {
        setLogo(urlResponse.url.href);
        console.info("logo url", urlResponse.url.href);
      });
    }
    if (user.config?.contact_details) {
      setContactDetails(user.config.contact_details);
    }
    if (user.config?.color_scheme) {
      updateColorStyle(user.config.color_scheme);
    }
  }, [user]);

  const cyclesInYears = useMemo(() => {
    if (!response || response?.results.length === 0) {
      return [];
    }

    const lastItem = response.results[response.results.length - 1];

    return lastItem.births.map((birth, index) => {
      return {
        cycle: `${index + 1} ${index === 0 ? "cycle" : "cycles"}`,
        eggs: lastItem.eggs[index].toString(),
        liveBirth1: birth[0],
        liveBirth2: birth[1],
      };
    });
  }, [response]);

  useEffect(() => {
    updateBreadcrumbs([
      {
        label: translations.report.breadcrumbs.eggFreezeCalculator,
        onClick: () => {
          navigate("/calc");
        },
      },
      {
        label: translations.report.breadcrumbs.report,
        onClick: () => {
          navigate("/calc");
        },
      },
    ]);

    updateActions([
      <Box width={180}>
        <Button
          fullWidth
          key={"export"}
          color={"blackButton"}
          onClick={exportToPDF}
        >
          {isLoading ? (
            <CircularProgress sx={{ color: colors.stone[500] }} />
          ) : (
            <Stack direction={"row"} spacing={1}>
              <FileDown />
              <Typography>
                {generalTranslations.report.buttons.export}
              </Typography>
            </Stack>
          )}
        </Button>
      </Box>,
      <Button key={"notes"} color={"blackButton"} onClick={toggleNotesDialog}>
        <Stack direction={"row"} spacing={1}>
          <NoteAdd />
          <Typography>{generalTranslations.report.buttons.addNotes}</Typography>
        </Stack>
      </Button>,
    ]);

    return () => {
      updateActions([]);
      updateBreadcrumbs([]);
    };
  }, [logo, formData, response, isLoading]);

  const exportToPDF = useCallback(async () => {
    const pdf = new PDF(
      theme.palette.reportColorStyles[
        colorStyle as ReportColorStyleName
      ] as ReportColorStyle,
      translations.report.title,
      contactDetails,
      800
    );

    setLoading(true);

    pdf.setDate(new Date());
    const logoElm = document.getElementById("report-logo") as HTMLImageElement;

    let logoWidth = 64;
    let logoHeight = 64;
    try {
      const originalWidth = logoElm?.naturalWidth ?? logoElm?.clientWidth ?? 64;
      const originalHeight =
        logoElm?.naturalHeight ?? logoElm?.clientHeight ?? 64;

      logoWidth = Math.min(originalWidth, 64);
      logoHeight = (logoWidth / originalWidth) * originalHeight;

      console.info("logo width and height", logoWidth, logoHeight);
    } catch (error) {
      showError(translations.report.error.logoRetrieval);
    }

    const rows = [
      {
        columns: [
          {
            key: translations.report.patientInfo.name,
            value: formData?.name ?? "",
          },
          {
            key: translations.report.patientInfo.ethnicity,
            value: ethnicities.join(", "),
          },
        ],
      },
      {
        columns: [
          {
            key: translations.report.patientInfo.weight,
            value: `${formData?.weight}`,
          },
          {
            key: translations.report.patientInfo.height,
            value: `${formData?.height}`,
          },
        ],
      },
      {
        columns: [
          {
            key: translations.report.patientInfo.age,
            value: `${formData?.age}`,
          },
          {
            key: translations.report.patientInfo.amh,
            value: formData?.amh ? `${formData?.amh.toFixed(2)} ng/ml` : "-",
          },
        ],
      },
    ];

    if (!formData?.amh) {
      rows[2].columns.pop();
    }

    if (!formData?.ethnicity?.length) {
      rows[0].columns.pop();
    }

    if (notes) {
      rows[2].columns.push({
        key: translations.report.patientInfo.notes,
        value: notes,
      });
    }

    pdf.setPatientInformation({
      rows: rows,
    });

    pdf.setLogo(null);
    pdf.build();

    console.info("include sections", selectedSections);

    pdf.addHtmlCanvas(logoElm, {}, logoWidth, null, 18, 18);
    const bulpIconByColor: { [key: string]: string } = {
      black: LightBulbBlack,
      blue: LightBulbBlue,
      green: LightBulbGreen,
      orange: LightBulbOrange,
      pink: LightBulbPink,
      purple: LightBulbPurple,
      red: LightBulbRed,
      yellow: LightBulbYellow,
    };

    pdf.addMargins(1);

    await pdf.addTextWithIcon(
      translations.report.sections.yourEstimatedResults,
      translations.report.texts.estimatedResults,
      bulpIconByColor[colorStyle],
      20,
      "#ffffff",
      getCurrentColorScheme(theme, colorStyle).text
    );
    await pdf.addHtmlCanvas(
      document.getElementById("estimatedResultsIn5Years")!,
      {},
      559,
      null,
      18
    );
    pdf.addPage();
    pdf.addHtmlCanvas(logoElm, {}, logoWidth, null, 18, 18);
    await pdf.addHtmlCanvas(
      document.querySelector(
        "#chartAttritionRates .MuiPaper-root:nth-child(2)"
      )!
    );
    pdf.addMargins(2);
    pdf.addSubTitle(translations.report.subtitles.whyManyEggs);
    pdf.addMargins(0.5);
    pdf.addText(translations.report.texts.ivfProcess, "serif", 12, "#646767");
    pdf.addMargins(4);

    pdf.addSubTitle(translations.report.subtitles.averageDecreaseEggs);
    pdf.addMargins(0.5);
    pdf.addText(
      translations.report.texts.attritionRates,
      "serif",
      12,
      "#646767"
    );
    pdf.addMargins(4);

    pdf.addPage();
    pdf.addHtmlCanvas(logoElm, {}, logoWidth, null, 18, 18);
    pdf.addSubTitle(
      translations.report.subtitles.compareAgeGroup.replace(
        "{age}",
        formData?.age.toString()
      )
    );
    debugger;
    pdf.addMargins(0.5);
    await pdf.addHtmlCanvas(document.getElementById("chartEggsByAge")!);
    pdf.addMargins(2);

    pdf.addPage();
    pdf.addHtmlCanvas(logoElm, {}, logoWidth, null, 18, 18);
    pdf.addSubTitle(translations.report.subtitles.averageEggsRetrieved);
    pdf.addMargins(0.5);
    pdf.addText(translations.report.texts.eggsByAge, "serif", 12, "#646767");
    pdf.addMargins(1);

    await pdf.addHtmlCanvas(document.getElementById("averageEggs")!);

    setLoading(false);

    pdf.save(`Egg_Freezing_${user.clinic}.pdf`);
  }, [logo, formData, response, footerRef, contactDetails, colorStyle]);

  const averageEggsData = useMemo(() => {
    if (!response) {
      return [];
    }
    const ageMax = 45;
    const ageMin = 18;
    const ageRange = Array.from(
      { length: ageMax - ageMin + 1 },
      (_, i) => ageMin + i
    );

    const data: AverageEggsChartData[] = ageRange.map((age) => {
      const eggs = 20; // Replace with a default or calculated value if needed
      return {
        age: age,
        numEggs: eggs,
        numEggsLow: eggs * 0.8,
        numEggsHigh: eggs * 1.2,
        error: [
          Math.max(0, -0.0073 * Math.pow(age, 2) + 0.0835 * age + 13.247), // Lower polynomial
          Math.max(0, -0.0129 * Math.pow(age, 2) + 0.168 * age + 22.5367), // Upper polynomial
        ],
        lowerError: Math.max(
          0,
          -0.0073 * Math.pow(age, 2) + 0.0835 * age + 13.247
        ), // Lower polynomial
        upperError: Math.max(
          0,
          -0.0129 * Math.pow(age, 2) + 0.168 * age + 22.5367
        ),
      };
    });

    return data;
  }, [response]);

  const handleSaveNotes = (notes: string) => {
    setNotes(notes);
  };

  return (
    <>
      <ReportWrapper
        onIncludeChange={(includedSections) => {}} // deprecated
        type={"report"}
        title={translations.report.title}
        sections={[
          {
            title: translations.report.sections.patientDetails,
            component: (
              <PatientInfoCard
                notes={notes}
                type="report"
                patientInfo={{
                  name: formData?.name ?? "",
                  weight: `${formData?.weight ?? ""} kg`,
                  age: `${formData?.age ?? 0}`,
                  ethnicity: ethnicities.join(", "),
                  height: `${formData?.height ?? ""} cm`,
                  amh: formData?.amh
                    ? `${formData?.amh?.toFixed(2)} ng/ml`
                    : null,
                }}
              />
            ),
          },
          {
            id: "estimatedResults",
            title: translations.report.sections.yourEstimatedResults,
            component: (
              <InfoCard content={translations.report.texts.estimatedResults} />
            ),
          },
          {
            id: "estimatedResultsIn5Years",
            component: (
              <Stack
                justifyContent={"space-between"}
                direction={"row"}
                spacing={2}
              >
                <CycleCard
                  cycle={1}
                  eggs={response?.results[0].eggs[0] ?? 0}
                  liveBirthChance={response?.results[0].births[0] ?? [0, 0]}
                />
                <CycleCard
                  cycle={2}
                  eggs={response?.results[0].eggs[1] ?? 0}
                  liveBirthChance={response?.results[0].births[1] ?? [0, 0]}
                />
                <CycleCard
                  cycle={3}
                  eggs={response?.results[0].eggs[2] ?? 0}
                  liveBirthChance={response?.results[0].births[2] ?? [0, 0]}
                />
              </Stack>
            ),
          },
          {
            id: "chartAttritionRates",
            title: translations.report.sections.attritionRate,
            component: (
              <Stack>
                <Box>
                  <CycleTable cycleData={cyclesInYears} />
                </Box>

                <Stack sx={{ marginTop: 2 }} gap={1}>
                  <Typography variant="report.title">
                    {translations.report.subtitles.whyManyEggs}
                  </Typography>
                  <Typography variant="report.info.label" color={"grey"}>
                    {translations.report.texts.eggsByAge}
                  </Typography>
                </Stack>
              </Stack>
            ),
          },
          {
            id: "chartEggsByAge",
            title: translations.report.sections.attritionRate,
            component: response?.results[0].attrition ? (
              <ProcessSteps
                age={response?.results[0].eggs[0] ?? 0}
                attrition={response?.results[0].attrition}
                attritionGraph={response?.results[0].attrition_graph}
              />
            ) : (
              <></>
            ),
          },
          {
            title: "",
            component: (
              <Stack spacing={1}>
                <Typography variant="report.title">
                  {translations.report.sections.compareAgeGroupTitle}
                </Typography>
                <Typography variant="report.info.label">
                  {translations.report.sections.compareAgeGroupDescription}
                </Typography>
              </Stack>
            ),
          },
          {
            id: "averageEggs",
            title: translations.report.sections.averageEggs,
            component: (
              <Box>
                <AverageEggsChart
                  age={formData?.age ?? 0}
                  eggs={response?.results[0].eggs[0] ?? 0}
                  data={averageEggsData}
                  width={reportPageWidth}
                  height={500}
                />
              </Box>
            ),
          },
        ]}
      />
      <NotesDialog onSave={handleSaveNotes} />
    </>
  );
};

export default Report;
